/**
 * Contracts shared by app, admin, api, and workers.
 *
 * There is no TypeScript in this project (specs.md §1a), so a contract is
 * expressed two ways: frozen runtime constants that code can actually check
 * against, and JSDoc typedefs that editors use for autocompletion. The
 * constants are the part that matters — they are what a test or a validator
 * asserts on, and they cannot silently drift the way a comment can.
 *
 * Keep this package free of runtime dependencies and of anything
 * environment-specific: it is imported by Workers, by browsers, and by tests.
 */

/** Roles. Authorization is enforced server-side for every one of these. */
export const ROLES = Object.freeze(['parent', 'student', 'support', 'admin']);

/** Supported interface languages. Arabic is the default. */
export const LANGUAGES = Object.freeze(['ar', 'en']);

/** Writing direction per language, so nothing has to infer it from a list. */
export const DIRECTIONS = Object.freeze({ ar: 'rtl', en: 'ltr' });

/** The language used when nothing else is known. */
export const DEFAULT_LANGUAGE = 'ar';

/**
 * Narrows any input to a supported language.
 * @param {unknown} value
 * @returns {'ar'|'en'|null} null when unsupported, so callers can fall through
 */
export function toLanguage(value) {
  const candidate = String(value ?? '').toLowerCase().slice(0, 2);
  return LANGUAGES.includes(candidate) ? candidate : null;
}

/**
 * Machine-readable validation reasons.
 *
 * The API returns these alongside a human message so a client can render the
 * text in the user's own language. Without them, an English-speaking parent
 * would get Arabic validation errors from the server no matter what the
 * interface language is set to.
 */
export const VALIDATION_CODES = Object.freeze({
  REQUIRED: 'required',
  INVALID_EMAIL: 'invalid_email',
  PASSWORD_TOO_SHORT: 'password_too_short',
  PASSWORD_INVALID: 'password_invalid',
  USERNAME_FORMAT: 'username_format',
  USERNAME_TAKEN: 'username_taken',
  GRADE_INVALID: 'grade_invalid',
  LANGUAGE_INVALID: 'language_invalid',
  NO_CHANGES: 'no_changes',
});

/**
 * Lesson states. Only the API transitions these, and every transition writes an
 * event. The model never advances lesson state.
 */
export const LESSON_STATES = Object.freeze([
  'PLANNED',
  'WARM_UP',
  'EXPLANATION',
  'GUIDED_PRACTICE',
  'INDEPENDENT_PRACTICE',
  'EXIT_TICKET',
  'REFLECTION',
  'REMEDIATION',
  'PAUSED',
  'COMPLETED',
]);

/**
 * Mastery bands shown to parents. Deliberately coarse: an over-precise
 * probability implies confidence the evidence does not support.
 */
export const MASTERY_BANDS = Object.freeze([
  'NOT_STARTED',
  'NEEDS_INSTRUCTION',
  'DEVELOPING',
  'MOSTLY_INDEPENDENT',
  'MASTERED',
  'REVIEW_DUE',
]);

/**
 * How much help a correct answer required. This distinction is the product's
 * core promise: guided success is never recorded as independent mastery.
 */
export const ASSISTANCE_LEVELS = Object.freeze([
  'independent',
  'after_hint',
  'after_worked_example',
]);

/** Stable, client-facing error codes. Messages may change; codes may not. */
export const ERROR_CODES = Object.freeze([
  'unauthenticated',
  'forbidden',
  'not_found',
  'conflict',
  'validation_failed',
  'rate_limited',
  'internal_error',
  'service_unavailable',
]);

/** Grades this product supports. Anything else is rejected at the boundary. */
export const GRADES = Object.freeze(['4', '5', '6']);

/**
 * The catalogue taxonomy.
 *
 * A parent finds their child's curriculum by narrowing: country, then the
 * education track that country runs, then the grade, then the subject. That is
 * the order a parent actually thinks in — "we're in Egypt, on the American
 * diploma, grade six, maths" — and it is the order the browse UI asks in.
 *
 * These are frozen lists rather than a table because they are a *vocabulary*,
 * not data: adding a country is a product decision that comes with translations
 * and a marketing claim, so it should arrive in a commit rather than a form.
 * The moment that stops being true, they become rows.
 *
 * Deliberately no year. A curriculum edition changes rarely, a year multiplies
 * every catalogue entry, and nothing in the product needs it yet.
 */
export const COUNTRIES = Object.freeze(['eg', 'sa']);

/**
 * Education tracks.
 *
 * Not every track exists in every country, and the browse UI only offers the
 * ones that actually have published curricula — derived from the catalogue
 * rather than declared here, so the list can never promise something empty.
 */
export const TRACKS = Object.freeze(['national', 'american', 'british', 'ib', 'azhar']);

/** Subjects a curriculum can teach. */
export const SUBJECTS = Object.freeze(['math', 'science', 'arabic', 'english', 'social_studies']);

/**
 * Grades the catalogue may carry.
 *
 * Wider than GRADES, which is what a *student profile* accepts today. The two
 * are separate on purpose: the catalogue can hold a grade-9 book before the
 * product opens to grade 9, and narrowing the student list is a product gate
 * rather than a data one.
 */
export const CATALOGUE_GRADES = Object.freeze([
  '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12',
]);

/** Narrows any input to a known catalogue value, or null. */
export function toCatalogueTerm(kind, value) {
  const lists = {
    country: COUNTRIES,
    track: TRACKS,
    subject: SUBJECTS,
    grade: CATALOGUE_GRADES,
  };
  const list = lists[kind];
  if (!list) return null;
  const candidate = String(value ?? '').trim().toLowerCase();
  return list.includes(candidate) ? candidate : null;
}

/**
 * Field limits, shared so the browser and the API enforce the same bounds and a
 * parent never fills in a field the server will silently truncate.
 */
export const LIMITS = Object.freeze({
  parentName: 120,
  studentName: 80,
  username: 32,
  email: 254,
  password: Object.freeze({ min: 10, max: 200 }),
  country: 100,
  curriculum: 300,
  challenge: 1500,
});

/**
 * Student usernames are lowercase letters, digits, dot, underscore, and hyphen.
 * Deliberately narrow: a child types this on a tablet, and a confusable
 * username becomes a support ticket.
 */
export const USERNAME_PATTERN = /^[a-z0-9._-]{3,32}$/;

/** Pragmatic email check. Real validation is delivery, not a regular expression. */
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Returns the problems with a password as stable reason codes. Empty means
 * acceptable.
 *
 * Length carries the weight here; composition rules mostly push people toward
 * predictable substitutions without adding real entropy.
 *
 * @param {string} password
 * @returns {string[]}
 */
export function passwordProblems(password) {
  const problems = [];
  const value = String(password ?? '');
  if (value.length < LIMITS.password.min) problems.push('too_short');
  if (value.length > LIMITS.password.max) problems.push('too_long');
  if (/^\s|\s$/.test(value)) problems.push('surrounding_whitespace');
  return problems;
}

/**
 * Normalizes an email for storage and comparison.
 * @param {string} email
 * @returns {string}
 */
export function normalizeEmail(email) {
  return String(email ?? '')
    .trim()
    .toLowerCase()
    .slice(0, LIMITS.email);
}

/**
 * Normalizes a student username for storage and comparison.
 * @param {string} username
 * @returns {string}
 */
export function normalizeUsername(username) {
  return String(username ?? '')
    .trim()
    .toLowerCase()
    .slice(0, LIMITS.username);
}

/**
 * @typedef {object} ApiErrorBody
 * @property {string} code One of ERROR_CODES.
 * @property {string} message Safe to show a user.
 * @property {string} request_id Correlates with server logs.
 * @property {Array<{field?: string, message: string}>} [details]
 */

/**
 * @typedef {object} Parent
 * @property {string} id
 * @property {string} email
 * @property {string} name
 * @property {'ar'|'en'} language
 * @property {boolean} email_verified
 * @property {string} created_at ISO 8601, UTC.
 */

/**
 * @typedef {object} Student
 * @property {string} id
 * @property {string} parent_id
 * @property {string} name Display name; may be a nickname.
 * @property {string} username What the child signs in with.
 * @property {'4'|'5'|'6'} grade
 * @property {'ar'|'en'} language
 * @property {string} created_at ISO 8601, UTC.
 */
